import {
  Flex,
  HStack,
  useMediaQuery,
  RadioProps,
  useRadioGroup,
  Text,
} from "@chakra-ui/react";
import { RadioCard } from "../../../../molecules/RadioCard/NumberRadioCard";
import { useEffect, useMemo, useState } from "react";
import { FormControlQuestionSearch } from "../../../../molecules/FormControl/FormControlQuestionSearch";
import { FormatReplic } from "../../../../templates/FormatReplic";
import { FormErrorMessageDefault } from "../../../../atoms/FormErrorMessage/FormErrorMessageDefault";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import { nextStep } from "../../../../../utils/nextStep";
import { ClearButton } from "../../../../atoms/Button/ClearButton";

interface IProps extends RadioProps {
  name: string;
  label: string;
  has_promoter_replica?: boolean;
  has_neutral_replica?: boolean;
  has_detractor_replica?: boolean;
  promoter_replica?: string;
  neutral_replica?: string;
  detractor_replica?: string;
  selectColor: string;
  setValue: (nps: number, replica: string) => void;
  description?: string;
  error?:
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | boolean
    | undefined;
  defaultValue?: number;
  defaultReplica?: string;
  step?: number;
  setStep?: (value: number) => void;
  handleTrigger?: (value: string) => void;
  questionId?: string;
  updateError?: (value: string, error: boolean) => void;
}

export function NPSRadioQuestionDefault(props: IProps) {
  const [hasError, setHasError] = useState<boolean>();
  const [replica, setReplica] = useState<string>(props.defaultReplica ?? "");
  const [value, setValue] = useState<number>(props.defaultValue ?? -1);
  const [isLargerThan360] = useMediaQuery("(min-width: 360px)");
  const [isLargerThan500] = useMediaQuery("(min-width: 500px)");
  const [isLargerThan600] = useMediaQuery("(min-width: 600px)");
  const options = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
  const colors = [
    "#fc514c",
    "#fc5a5e",
    "#fe755f",
    "#fe9459",
    "#ffb256",
    "#fecb3c",
    "#eecd39",
    "#edce3b",
    "#96d22b",
    "#55d31e",
    "#1db722",
  ];

  const sortingValue = useMemo(() => {
    if (value >= 9) {
      return "promoter";
    } else if (value <= 6 && value >= 0) {
      return "detractor";
    } else if (value === 7 || value === 8) {
      return "neutral";
    }
    return "";
  }, [value]);

  const [focus, setFocus] = useState<boolean>(false);

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: props.name,
    value: String(value),
    onChange: (newValue) => {
      setValue(Number(newValue));
      props.setValue(Number(newValue), replica);
      if (props.updateError && props.questionId && props.error) {
        if (props.questionId) {
          props.updateError(
            `quizResponses.${props.questionId}`,
            props.error as boolean
          );
        } else if (!props.questionId) {
          props.updateError(`nps`, props.error as boolean);
        }
      }
      if (props.step !== undefined && props.setStep !== undefined) {
        const checkNextStep = nextStep({
          checkValue: newValue,
          has_replica_detractor: props.has_detractor_replica,
          has_replica_neutral: props.has_neutral_replica,
          has_replica_promoter: props.has_promoter_replica,
          type: "NPS",
        });
        if (checkNextStep) {
          props.setStep(props.step + 1);
          if (props.handleTrigger) {
            props.handleTrigger(`nps`);
          }
        }
      }
    },
  });

  const group = getRootProps();

  useEffect(() => {
    setHasError(!!props.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.error]);

  const widthRadio = useMemo(() => {
    if (!isLargerThan360) {
      return "40px";
    }

    if (!isLargerThan500) {
      return "30px";
    }

    if (!isLargerThan600) {
      return "35px";
    }

    return "40px";
  }, [isLargerThan360, isLargerThan500, isLargerThan600]);

  return (
    <>
      <FormControlQuestionSearch
        label={props.label}
        name={props.name}
        isRequired={props.isRequired}
        description={props.description}
      >
        {hasError && <FormErrorMessageDefault error="Questão obrigatória" />}
        <Flex
          justifyContent="center"
          alignItems="center"
          direction="row"
          flexWrap={!isLargerThan360 ? "wrap" : "inherit"}
        >
          <Flex mb={1.5} mt={1.5}>
            {options
              .filter((option) => {
                if (!isLargerThan360) {
                  return Number(option) < 6;
                }
                return true;
              })
              .map((option) => {
                const radio = getRadioProps({ value: option });

                return (
                  <RadioCard
                    key={option}
                    {...radio}
                    color={colors[Number(option)]}
                    borderColor={colors[Number(option)]}
                    bg="#fff"
                    selectColor={colors[Number(option)]}
                    width={widthRadio}
                  >
                    <Text fontWeight="bold">{option}</Text>
                  </RadioCard>
                );
              })}
          </Flex>
          <Flex mb={1.5} mt={1.5}>
            {options
              .filter((option) => {
                if (!isLargerThan360) {
                  return Number(option) >= 6;
                }
                return false;
              })
              .map((option) => {
                const radio = getRadioProps({ value: option });

                return (
                  <RadioCard
                    key={option}
                    {...radio}
                    color={colors[Number(option)]}
                    borderColor={colors[Number(option)]}
                    bg="#fff"
                    selectColor={colors[Number(option)]}
                    width={widthRadio}
                  >
                    <Text fontWeight="bold">{option}</Text>
                  </RadioCard>
                );
              })}
          </Flex>
        </Flex>
        <HStack wrap="wrap" justifyContent="center" {...group}></HStack>
      </FormControlQuestionSearch>
      <FormatReplic
        detractor_replica={props.detractor_replica}
        neutral_replica={props.neutral_replica}
        promoter_replica={props.promoter_replica}
        sortingValue={sortingValue}
        value={value}
        has_detractor_replica={props.has_detractor_replica}
        has_neutral_replica={props.has_neutral_replica}
        has_promoter_replica={props.has_promoter_replica}
        focus={focus}
        color={props.selectColor}
        setValue={(value: any, result: string) => props.setValue(value, result)}
        setReplica={(value: string) => setReplica(value)}
        setFocus={(value: boolean) => setFocus(value)}
        defaultReplica={replica}
      />
      {!props.isRequired && value !== -1 && (
        <ClearButton
          onClick={() => {
            setValue(-1);
            setReplica("");
            setFocus(false);
            props.setValue(-1, "");
          }}
        />
      )}
    </>
  );
}
